// src/components/ProductionLogViewer.js
import React, { useRef, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  startAfter,
  getDocs,
  limit as qLimit,
} from 'firebase/firestore';
import { saveAs } from 'file-saver';

const PAGE_SIZE = 50;

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`; // "YYYY-MM-DD"
}

function oneMonthAgoToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const mAgo = new Date(d);
  mAgo.setMonth(mAgo.getMonth() - 1); // calendar month back
  return { start: fmt(mAgo), end: fmt(d) };
}

function csvEscape(value) {
  const s = String(value ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export default function ProductionLogViewer() {
  const [rows, setRows] = useState([]);
  const [flavourContains, setFlavourContains] = useState(''); // substring filter (client-side)
  const [startDate, setStartDate] = useState(''); // "YYYY-MM-DD"
  const [endDate, setEndDate] = useState(''); // "YYYY-MM-DD"
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc'); // show newest first
  const lastDocRef = useRef(null); // DocumentSnapshot for paging

  const buildQuery = ({ loadMore = false } = {}) => {
    const colRef = collection(db, 'production_logs');
    let qRef = colRef;

    const hasText = flavourContains.trim().length > 0;
    const hasDates = Boolean(startDate || endDate);
    const defaultLast50 = !hasText && !hasDates;

    // Effective date window
    let effStart = startDate || '';
    let effEnd = endDate || '';

    // If user typed text but gave no dates → default to last month
    if (hasText && !hasDates) {
      const { start, end } = oneMonthAgoToday();
      effStart = start;
      effEnd = end;
    }

    // If default last 50 (no text, no dates): no where() — just orderBy desc + limit
    if (!defaultLast50) {
      if (effStart) qRef = query(qRef, where('date', '>=', effStart));
      if (effEnd) qRef = query(qRef, where('date', '<=', effEnd));
    }

    // Order newest first for all cases
    qRef = query(qRef, orderBy('date', 'desc'));

    // Pagination cursor after orderBy
    if (loadMore && lastDocRef.current) {
      qRef = query(qRef, startAfter(lastDocRef.current));
    }

    // Limit
    qRef = query(qRef, qLimit(PAGE_SIZE));

    return { qRef };
  };

  const runSearch = async ({ loadMore = false } = {}) => {
    setLoading(true);
    try {
      const { qRef } = buildQuery({ loadMore });
      const snap = await getDocs(qRef);
      let newRows = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Client-side "contains" filter (case-insensitive) on the fetched window
      const needle = flavourContains.trim().toLowerCase();
      if (needle) {
        newRows = newRows.filter(r =>
          (r.flavour || '').toLowerCase().includes(needle)
        );
      }

      lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.size === PAGE_SIZE);
      setRows(prev => (loadMore ? [...prev, ...newRows] : newRows));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    lastDocRef.current = null;
    runSearch({ loadMore: false });
  };

  const resetAll = () => {
    setFlavourContains('');
    setStartDate('');
    setEndDate('');
    setRows([]);
    setHasMore(false);
    lastDocRef.current = null;
  };

  const downloadCSV = async () => {
    if (!startDate || !endDate) {
      alert('Please enter both a start date and an end date.');
      return;
    }

    if (startDate > endDate) {
      alert('Start date cannot be after end date.');
      return;
    }

    setDownloading(true);

    try {
      const allRows = [];
      let lastDoc = null;
      let keepGoing = true;

      while (keepGoing) {
        let qRef = query(
          collection(db, 'production_logs'),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc'),
          qLimit(PAGE_SIZE)
        );

        if (lastDoc) {
          qRef = query(
            collection(db, 'production_logs'),
            where('date', '>=', startDate),
            where('date', '<=', endDate),
            orderBy('date', 'desc'),
            startAfter(lastDoc),
            qLimit(PAGE_SIZE)
          );
        }

        const snap = await getDocs(qRef);
        const pageRows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        allRows.push(...pageRows);

        if (snap.size < PAGE_SIZE) {
          keepGoing = false;
        } else {
          lastDoc = snap.docs[snap.docs.length - 1];
        }
      }

      // Optional flavour filter for CSV too, if user typed something
      const needle = flavourContains.trim().toLowerCase();
      let filteredRows = allRows;

      if (needle) {
        filteredRows = allRows.filter(r =>
          (r.flavour || '').toLowerCase().includes(needle)
        );
      }

      if (!filteredRows.length) {
        alert('No production logs found in that date range.');
        return;
      }

      // Match current UI sort
      filteredRows.sort((a, b) => {
        const da = a.date ?? '';
        const db = b.date ?? '';
        if (da < db) return sortOrder === 'asc' ? -1 : 1;
        if (da > db) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      const headers = ['Date', 'Flavour', 'Quantity'];
      const body = filteredRows.map(r =>
        [
          csvEscape(r.date),
          csvEscape(r.flavour),
          csvEscape(r.quantity),
        ].join(',')
      );

      const csv = [headers.join(','), ...body].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `production_logs_${startDate}_to_${endDate}.csv`);
    } catch (err) {
      console.error('CSV download failed:', err);
      alert('Failed to download CSV.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSortByDate = () => {
    const next = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(next);
    setRows(prev => {
      const copy = [...prev];
      copy.sort((a, b) => {
        const da = a.date ?? '';
        const db = b.date ?? '';
        if (da < db) return next === 'asc' ? -1 : 1;
        if (da > db) return next === 'asc' ? 1 : -1;
        return 0;
      });
      return copy;
    });
  };

  // styles
  const thStyle = {
    borderBottom: '1px solid #ccc',
    padding: '10px',
    textAlign: 'center',
    background: '#f0f0f0',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const tdStyle = {
    borderBottom: '1px solid #eee',
    padding: '10px',
    textAlign: 'center',
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, textAlign: 'center', margin: 'auto' }}>
      <h2>📦 Production Log Viewer</h2>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Flavour contains… (case-insensitive)"
          value={flavourContains}
          onChange={e => setFlavourContains(e.target.value)}
        />
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          aria-label="Start date"
        />
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          aria-label="End date"
        />
        <button type="submit" disabled={loading || downloading}>
          {loading ? 'Searching…' : '🔍 Search'}
        </button>
        <button type="button" onClick={resetAll} disabled={loading || downloading}>
          🔄 Reset
        </button>
        <button
          type="button"
          onClick={downloadCSV}
          disabled={loading || downloading || !startDate || !endDate}
        >
          {downloading ? 'Downloading…' : '⬇️ Download CSV'}
        </button>
      </form>

      <div style={{ marginBottom: 8, color: '#555' }}>
        {rows.length ? `Showing ${rows.length}${hasMore ? '+' : ''} result(s)` : 'No results yet — run a search.'}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
        <thead>
          <tr>
            <th style={thStyle} onClick={handleSortByDate}>
              Date {sortOrder === 'asc' ? '▲' : '▼'}
            </th>
            <th style={thStyle}>Flavour</th>
            <th style={thStyle}>Quantity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td style={tdStyle}>{r.date}</td>
              <td style={tdStyle}>{r.flavour}</td>
              <td style={tdStyle}>{r.quantity}</td>
            </tr>
          ))}
          {!rows.length && !loading && (
            <tr>
              <td colSpan="3" style={{ padding: 16, textAlign: 'center', color: '#777' }}>
                No results.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {hasMore && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => runSearch({ loadMore: true })} disabled={loading || downloading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}